<script setup lang="ts">
/**
 * ImportCsvModal — 1:1 порт docs/mockups/modal-import-csv.html (inbox-154).
 *
 * 4-step wizard:
 *   1. Upload — drop-zone / file picker, client-side parse header + row count
 *   2. Mapping — auto-detect CSV columns → link-platform fields
 *      (target_url required, slug/title/tags optional)
 *   3. Preview — первые 5 строк превью с маппингом
 *   4. Done — summary (imported / duplicates / errors)
 *
 * Backend endpoint /api/v1/links/bulk-import пока не реализован — wrap'ит
 * actual submit в UiDevCard message с pointer на отдельную inbox-task.
 *
 * Open: modals.open('import-csv').
 */

import { computed, ref, watch } from 'vue';
import { useModalStore } from '@/stores/modal';
import UiModal from '@/components/ui/UiModal.vue';
import UiButton from '@/components/ui/UiButton.vue';
import UiDevCard from '@/components/ui/UiDevCard.vue';

const modals = useModalStore();

type Step = 1 | 2 | 3 | 4;
type FieldKey = 'target_url' | 'slug' | 'title' | 'tags' | 'ignore';

interface ParsedCsv {
  fileName: string;
  fileSize: number;
  encoding: string;
  delimiter: string;
  headers: string[];
  rows: string[][];
}

const FIELDS: { key: FieldKey; label: string; required?: boolean }[] = [
  { key: 'target_url', label: 'Target URL', required: true },
  { key: 'slug', label: 'Slug (короткий код)' },
  { key: 'title', label: 'Заголовок' },
  { key: 'tags', label: 'Теги (через ;)' },
  { key: 'ignore', label: 'Игнорировать' },
];

const step = ref<Step>(1);
const csv = ref<ParsedCsv | null>(null);
const mapping = ref<Record<number, FieldKey>>({});
const dragging = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);
const submitting = ref(false);
const result = ref<{ imported: number; duplicates: number; errors: number } | null>(null);

watch(
  () => modals.activeKey,
  (k) => {
    if (k !== 'import-csv') return;
    // Reset state на каждое открытие
    step.value = 1;
    csv.value = null;
    mapping.value = {};
    dragging.value = false;
    submitting.value = false;
    result.value = null;
  },
);

function onDrop(e: DragEvent) {
  e.preventDefault();
  dragging.value = false;
  const f = e.dataTransfer?.files?.[0];
  if (f) void handleFile(f);
}

function onPick() {
  fileInput.value?.click();
}

function onFileChange(e: Event) {
  const f = (e.target as HTMLInputElement).files?.[0];
  if (f) void handleFile(f);
}

async function handleFile(f: File) {
  if (!/\.(csv|txt)$/i.test(f.name)) {
    alert('Поддерживается только CSV / TXT.');
    return;
  }
  const text = await f.text();
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length === 0) {
    alert('Файл пустой.');
    return;
  }
  const delimiter = lines[0]!.includes(';') ? ';' : ',';
  const headers = lines[0]!.split(delimiter).map((h) => h.trim());
  const rows = lines.slice(1).map((l) => l.split(delimiter).map((c) => c.trim()));
  csv.value = {
    fileName: f.name,
    fileSize: f.size,
    encoding: 'UTF-8',
    delimiter,
    headers,
    rows,
  };
  // Auto-detect mapping
  const newMap: Record<number, FieldKey> = {};
  headers.forEach((h, i) => {
    const low = h.toLowerCase();
    if (low.includes('url') || low === 'target' || low === 'link') newMap[i] = 'target_url';
    else if (low === 'slug' || low === 'code' || low === 'short') newMap[i] = 'slug';
    else if (low === 'name' || low === 'title' || low === 'описание') newMap[i] = 'title';
    else if (low === 'tags' || low === 'теги') newMap[i] = 'tags';
    else newMap[i] = 'ignore';
  });
  mapping.value = newMap;
  step.value = 2;
}

const hasTargetUrl = computed(() =>
  Object.values(mapping.value).includes('target_url'),
);

const previewRows = computed(() => csv.value?.rows.slice(0, 5) || []);

function next() {
  if (step.value === 2 && !hasTargetUrl.value) {
    alert('Минимум одна колонка должна быть Target URL.');
    return;
  }
  if (step.value < 4) step.value = (step.value + 1) as Step;
}

function back() {
  if (step.value > 1) step.value = (step.value - 1) as Step;
}

async function submitImport() {
  if (!csv.value) return;
  submitting.value = true;
  // Backend endpoint /api/v1/links/bulk-import pending — stub'аем.
  await new Promise((r) => setTimeout(r, 800));
  const total = csv.value.rows.length;
  // Псевдо-rezultat для UI demonstration
  result.value = {
    imported: Math.max(0, total - 15),
    duplicates: 10,
    errors: 5,
  };
  submitting.value = false;
  step.value = 4;
  modals.emit('import-csv', result.value);
}

function fmtSize(b: number): string {
  if (b < 1024) return `${b} Б`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} КБ`;
  return `${(b / 1048576).toFixed(1)} МБ`;
}

function colLetter(i: number): string {
  return String.fromCharCode(65 + (i % 26));
}

function close() {
  if (!modals.pending) modals.close();
}
</script>

<template>
  <UiModal
    title="Импорт ссылок из CSV"
    subtitle="До 10 000 строк за раз. Дубликаты по target_url пропускаются по умолчанию."
    size="lg"
    :disable-close="submitting"
    @close="close"
  >
    <!-- Step indicator -->
    <nav class="steps" aria-label="Шаги импорта">
      <template v-for="(label, idx) in ['Загрузка', 'Маппинг колонок', 'Проверка', 'Готово']" :key="idx">
        <div
          :class="[
            'step',
            { active: step === idx + 1, done: step > idx + 1 },
          ]"
        >
          <div class="step-ico">
            <svg
              v-if="step > idx + 1"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              aria-hidden="true"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span v-else>{{ idx + 1 }}</span>
          </div>
          <div class="step-label">{{ label }}</div>
        </div>
        <div
          v-if="idx < 3"
          :class="['step-sep', { done: step > idx + 1 }]"
        />
      </template>
    </nav>

    <!-- Step 1: Upload -->
    <section v-if="step === 1" class="panel">
      <div
        :class="['drop-zone', { dragging }]"
        role="button"
        tabindex="0"
        @click="onPick"
        @keydown.enter="onPick"
        @dragover.prevent="dragging = true"
        @dragleave="dragging = false"
        @drop="onDrop"
      >
        <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        <div class="drop-title">Перетащите CSV сюда</div>
        <div class="drop-sub">или нажмите для выбора файла</div>
        <input
          ref="fileInput"
          type="file"
          accept=".csv,.txt,text/csv"
          hidden
          @change="onFileChange"
        />
      </div>
      <div class="upload-hint">
        Поддерживается UTF-8, разделители <code>,</code> или <code>;</code>. Первая строка — заголовки.
      </div>
    </section>

    <!-- Step 2: Mapping -->
    <section v-else-if="step === 2 && csv" class="panel">
      <div class="file-bar">
        <div class="file-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
        </div>
        <div class="file-meta">
          <div class="file-name">{{ csv.fileName }}</div>
          <div class="file-sub">
            <span>{{ fmtSize(csv.fileSize) }}</span><span class="bullet">·</span>
            <span><b>{{ csv.rows.length }}</b> строк</span><span class="bullet">·</span>
            <span>{{ csv.encoding }}</span><span class="bullet">·</span>
            <span>разделитель <code>{{ csv.delimiter }}</code></span>
          </div>
        </div>
      </div>

      <div class="section-head">
        <div>
          <div class="section-title">Маппинг колонок</div>
          <div class="section-sub">
            Сопоставьте столбцы CSV с полями ссылки.
            Автоопределено
            <b>
              {{ Object.values(mapping).filter((v) => v !== 'ignore').length }}
              из {{ csv.headers.length }}
            </b>
            столбцов.
          </div>
        </div>
      </div>

      <div class="map-table">
        <div class="map-thead">
          <div>CSV колонка</div>
          <div>Пример</div>
          <div>Поле link-platform</div>
        </div>
        <div v-for="(h, i) in csv.headers" :key="i" class="map-row" :class="{ matched: mapping[i] !== 'ignore' }">
          <div class="csv-col">
            <span class="col-letter">{{ colLetter(i) }}</span>
            <span class="col-name">{{ h }}</span>
          </div>
          <div class="example mono">{{ (csv.rows[0]?.[i] || '').slice(0, 40) }}</div>
          <div class="map-select">
            <select v-model="mapping[i]">
              <option v-for="f in FIELDS" :key="f.key" :value="f.key">
                {{ f.label }}{{ f.required ? ' · обязательное' : '' }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <p v-if="!hasTargetUrl" class="warning">
        ⚠ Минимум одна колонка должна быть «Target URL».
      </p>
    </section>

    <!-- Step 3: Preview -->
    <section v-else-if="step === 3 && csv" class="panel">
      <div class="section-head">
        <div>
          <div class="section-title">Превью первых 5 строк</div>
          <div class="section-sub">Проверьте, что значения попадают в правильные поля. Импортируем {{ csv.rows.length }} строк.</div>
        </div>
      </div>
      <div class="preview-table">
        <table>
          <thead>
            <tr>
              <th v-for="(_, i) in csv.headers" :key="i">
                {{ FIELDS.find((f) => f.key === mapping[i])?.label || '—' }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in previewRows" :key="idx">
              <td v-for="(c, i) in row" :key="i" :class="{ ignored: mapping[i] === 'ignore' }">
                {{ c.slice(0, 60) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <UiDevCard
        feature="Backend bulk-import endpoint"
        message="/api/v1/links/bulk-import пока не реализован — Submit использует stub (имитирует duplicate detection)."
      />
    </section>

    <!-- Step 4: Done -->
    <section v-else-if="step === 4 && result" class="panel">
      <div class="done-summary">
        <div class="summary-row">
          <div class="summary-num ok">{{ result.imported }}</div>
          <div class="summary-label">Импортировано ссылок</div>
        </div>
        <div class="summary-row">
          <div class="summary-num warn">{{ result.duplicates }}</div>
          <div class="summary-label">Дубликатов пропущено</div>
        </div>
        <div class="summary-row">
          <div class="summary-num err">{{ result.errors }}</div>
          <div class="summary-label">Ошибок</div>
        </div>
      </div>
      <p class="done-hint">Импорт завершён. Импортированные ссылки появятся в списке после обновления страницы.</p>
    </section>

    <template #footer>
      <UiButton v-if="step > 1 && step < 4" variant="ghost" :disabled="submitting" @click="back">
        ‹ Назад
      </UiButton>
      <div class="foot-spacer" />
      <UiButton v-if="step === 4" variant="primary" @click="close">Готово</UiButton>
      <template v-else>
        <UiButton v-if="step < 3" :disabled="step === 1 ? !csv : !hasTargetUrl" @click="next">Далее ›</UiButton>
        <UiButton v-else-if="step === 3" :loading="submitting" @click="submitImport">
          Импортировать {{ csv?.rows.length || 0 }} ссылок
        </UiButton>
      </template>
    </template>
  </UiModal>
</template>

<style scoped>
.steps {
  display: flex;
  align-items: center;
  padding: 12px 0 18px;
  border-bottom: 1px solid var(--border);
  margin-bottom: 16px;
}
.step {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--fg-muted);
}
.step-ico {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--bg-elev);
  border: 1px solid var(--border);
  display: grid;
  place-items: center;
  font-size: 12px;
  font-weight: 600;
}
.step.active .step-ico {
  background: var(--accent);
  color: #fff;
  border-color: var(--accent);
}
.step.done .step-ico {
  background: var(--success);
  color: #fff;
  border-color: var(--success);
}
.step.done .step-ico svg { width: 14px; height: 14px; }
.step-label { font-size: 12.5px; }
.step.active .step-label,
.step.done .step-label { color: var(--fg); font-weight: 500; }
.step-sep {
  flex: 1;
  height: 1px;
  background: var(--border);
  margin: 0 10px;
}
.step-sep.done { background: var(--success); }

.panel { display: flex; flex-direction: column; gap: 14px; }

.drop-zone {
  border: 2px dashed var(--border-strong);
  border-radius: 12px;
  padding: 56px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: var(--bg-elev);
  cursor: pointer;
  color: var(--fg-muted);
  transition: 0.12s;
}
.drop-zone:hover,
.drop-zone.dragging {
  border-color: var(--accent);
  background: var(--accent-soft);
  color: var(--accent-hi);
}
.drop-title { font-size: 15px; font-weight: 600; color: var(--fg); }
.drop-sub { font-size: 13px; }
.upload-hint { font-size: 12.5px; color: var(--fg-dim); text-align: center; }
.upload-hint code {
  background: var(--bg-elev);
  padding: 1px 5px;
  border-radius: 3px;
  font-family: 'SF Mono', Menlo, monospace;
}

.file-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 12px 14px;
}
.file-icon {
  width: 36px;
  height: 36px;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 7px;
  display: grid;
  place-items: center;
  color: var(--accent);
}
.file-icon svg { width: 18px; height: 18px; }
.file-name { font-weight: 500; color: var(--fg); }
.file-sub { font-size: 12px; color: var(--fg-muted); display: flex; flex-wrap: wrap; gap: 4px; align-items: center; }
.file-sub b { color: var(--fg); }
.file-sub .bullet { color: var(--fg-dim); }
.file-sub code { background: var(--bg); padding: 1px 5px; border-radius: 3px; font-family: 'SF Mono', Menlo, monospace; font-size: 11px; }

.section-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.section-title { font-size: 14px; font-weight: 600; color: var(--fg); }
.section-sub { font-size: 12.5px; color: var(--fg-muted); margin-top: 2px; }
.section-sub b { color: var(--fg); }

.map-table {
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
}
.map-thead {
  display: grid;
  grid-template-columns: 1fr 1fr 1.4fr;
  gap: 12px;
  padding: 10px 14px;
  background: var(--bg-elev);
  font-size: 11.5px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--fg-muted);
  font-weight: 600;
}
.map-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1.4fr;
  gap: 12px;
  padding: 10px 14px;
  border-top: 1px solid var(--border);
  align-items: center;
  font-size: 13px;
}
.csv-col { display: flex; align-items: center; gap: 8px; }
.col-letter {
  width: 22px;
  height: 22px;
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: 5px;
  font-size: 11px;
  font-weight: 600;
  display: grid;
  place-items: center;
  color: var(--fg-muted);
}
.col-name { color: var(--fg); font-family: 'SF Mono', Menlo, monospace; font-size: 12.5px; }
.example { color: var(--fg-muted); font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mono { font-family: 'SF Mono', Menlo, monospace; }
.map-select select {
  width: 100%;
  padding: 6px 10px;
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--fg);
  font: inherit;
  font-size: 12.5px;
}
.map-row.matched .col-name { color: var(--accent-hi); }

.warning {
  font-size: 12.5px;
  color: var(--warning);
  background: var(--warning-soft);
  border: 1px solid rgba(245, 165, 36, 0.3);
  border-radius: 7px;
  padding: 8px 12px;
  margin: 0;
}

.preview-table {
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: auto;
  max-height: 280px;
}
.preview-table table { width: 100%; border-collapse: collapse; }
.preview-table th,
.preview-table td {
  padding: 8px 12px;
  border-bottom: 1px solid var(--border);
  text-align: left;
  font-size: 12.5px;
  white-space: nowrap;
}
.preview-table th {
  background: var(--bg-elev);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--fg-muted);
  font-weight: 600;
}
.preview-table td.ignored { color: var(--fg-dim); }

.done-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
}
.summary-row {
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 16px;
  text-align: center;
}
.summary-num {
  font-size: 26px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  margin-bottom: 4px;
}
.summary-num.ok { color: var(--success); }
.summary-num.warn { color: var(--warning); }
.summary-num.err { color: var(--danger); }
.summary-label { font-size: 12px; color: var(--fg-muted); text-transform: uppercase; letter-spacing: 0.04em; }
.done-hint { font-size: 13px; color: var(--fg-muted); text-align: center; margin: 10px 0 0; }

.foot-spacer { flex: 1; }

@media (max-width: 760px) {
  .map-thead,
  .map-row { grid-template-columns: 1fr; }
  .done-summary { grid-template-columns: 1fr; }
}
</style>
